import React from 'react';
import { Camera, MapPin, LogIn, LogOut, Navigation, Flag } from 'lucide-react';

// ---------------------------------------------------------------------------
// STATIC UI MOCKUP (no real data). Public preview at /ui-preview/travel-distance
// Proposes: a dotted vertical timeline for a claim's Travel Selfies & GPS.
//   Start Selfie → (≈ km) → Place 1 Check-in → (≈ km) → Place 1 Check-out
//   → (≈ km from last checkout) → Place 2 Check-in → ...
// Each node shows GPS coords; each connector segment shows an approx distance.
// Shown for 1, 3 and 5 place visits.
// ---------------------------------------------------------------------------

const IST_GREEN = '#059669';
const IST_BLUE = '#2563eb';

// --- one node in the timeline -------------------------------------------------
function TimelineNode({ kind, title, subtitle, gps, time, img, distance, isLast }) {
  const cfg = {
    start:   { Icon: Flag,    dot: 'bg-emerald-500', ring: 'ring-emerald-100', label: 'text-emerald-700' },
    checkin: { Icon: LogIn,   dot: 'bg-emerald-500', ring: 'ring-emerald-100', label: 'text-emerald-700' },
    checkout:{ Icon: LogOut,  dot: 'bg-blue-500',    ring: 'ring-blue-100',    label: 'text-blue-700' },
  }[kind];
  const Icon = cfg.Icon;
  return (
    <div className="relative pl-12 pb-2">
      {/* dotted connector line */}
      {!isLast && (
        <span className="absolute left-[15px] top-7 bottom-0 border-l-2 border-dashed border-slate-300" aria-hidden />
      )}
      {/* node dot */}
      <span className={`absolute left-0 top-1 w-8 h-8 rounded-full ${cfg.dot} ring-4 ${cfg.ring} flex items-center justify-center text-white shadow`}>
        <Icon className="w-4 h-4" />
      </span>

      <div className="flex items-start gap-3">
        {img && (
          <img src={img} alt={title}
            className="w-14 h-14 rounded-lg object-cover border border-slate-200 shrink-0" />
        )}
        <div className="min-w-0">
          <p className={`text-xs font-bold uppercase tracking-wide ${cfg.label}`}>{title}</p>
          {subtitle && <p className="text-sm font-semibold text-slate-800 leading-tight">{subtitle}</p>}
          {time && <p className="text-[11px] text-slate-400">{time}</p>}
          <p className="text-[11px] text-slate-500 font-mono flex items-center gap-1 mt-0.5">
            <MapPin className="w-3 h-3 text-slate-400" /> {gps}
          </p>
        </div>
      </div>

      {/* distance segment badge (distance to the NEXT node) */}
      {distance && (
        <div className="ml-1 my-1.5 flex items-center gap-1.5">
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-full px-2 py-0.5">
            <Navigation className="w-3 h-3" /> {distance}
          </span>
          <span className="text-[10px] text-slate-400">{distance.includes('last checkout') ? '' : ''}</span>
        </div>
      )}
    </div>
  );
}

// Build a list of nodes from mock places. Distances are the segment to the NEXT node.
function buildNodes(start, places) {
  const nodes = [];
  // start selfie node — distance to first check-in
  nodes.push({
    kind: 'start', title: 'Travel Start Selfie', subtitle: 'Home / Origin',
    gps: start.gps, time: start.time, img: start.img,
    distance: places.length ? `≈ ${places[0].dFromPrev} km to ${places[0].name} check-in` : null,
  });
  places.forEach((p, i) => {
    const last = i === places.length - 1;
    nodes.push({
      kind: 'checkin', title: `${i + 1}. ${p.name} · Check-in`, subtitle: null,
      gps: p.inGps, time: p.inTime, img: p.inImg,
      distance: `≈ ${p.dInOut} km on-site → check-out`,
    });
    nodes.push({
      kind: 'checkout', title: `${i + 1}. ${p.name} · Check-out`, subtitle: null,
      gps: p.outGps, time: p.outTime, img: p.outImg,
      distance: last ? null : `≈ ${places[i + 1].dFromPrev} km from last checkout → ${places[i + 1].name}`,
      isLast: last,
    });
  });
  return nodes;
}

function TimelineCard({ label, start, places }) {
  const nodes = buildNodes(start, places);
  const totalKm = (places.reduce((s, p) => s + p.dFromPrev + p.dInOut, 0)).toFixed(1);
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 w-full">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-bold text-slate-900">{label}</h3>
        <span className="text-[11px] font-semibold text-slate-600 bg-slate-100 rounded-full px-2.5 py-1">
          {places.length} location{places.length > 1 ? 's' : ''} · route ≈ {totalKm} km
        </span>
      </div>
      <div>
        {nodes.map((n, i) => (
          <TimelineNode key={i} {...n} isLast={n.isLast || i === nodes.length - 1} />
        ))}
      </div>
    </div>
  );
}

const IMG = 'https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=100&h=100&fit=crop';
const IMG2 = 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop';

const START = { gps: '12.8828, 77.4705', time: '23/07/2026, 09:15', img: IMG };

const PLACE = (name, dFromPrev, dInOut, inGps, outGps) => ({
  name, dFromPrev, dInOut, inImg: IMG, outImg: IMG2,
  inGps, outGps,
  inTime: '23/07, 10:46', outTime: '23/07, 11:18',
});

const CASE_1 = [PLACE('K N Huindy', 8.2, 0.3, '12.9200, 77.5100', '12.9210, 77.5110')];
const CASE_3 = [
  PLACE('K N Huindy', 8.2, 0.3, '12.9200, 77.5100', '12.9210, 77.5110'),
  PLACE('Olavanna Fee Plaza', 42.6, 0.5, '11.2588, 75.7804', '11.2600, 75.7820'),
  PLACE('Vettichira Toll Plaza', 31.9, 0.2, '10.7867, 76.6548', '10.7870, 76.6550'),
];
const CASE_5 = [
  ...CASE_3,
  PLACE('Paliyekkara Toll Plaza', 55.1, 0.4, '10.4560, 76.2100', '10.4570, 76.2110'),
  PLACE('Paniyankara Toll Plaza', 28.7, 0.3, '11.2450, 75.8100', '11.2460, 75.8110'),
];

export default function TravelDistancePreview() {
  return (
    <div className="min-h-screen bg-neutral-100 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900">Travel Distance Timeline — UI Preview</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">
            Proposed layout for the claim's “Travel Selfies &amp; GPS” section: a dotted timeline from the
            start selfie through each place's check-in / check-out, showing GPS coordinates and the
            <span className="font-semibold text-indigo-700"> approx distance from the last checkout</span> to the next place.
            This is a static mockup with dummy data — nothing is wired to real claims yet.
          </p>
          <div className="mt-3 flex flex-wrap gap-3 text-[11px]">
            <span className="inline-flex items-center gap-1 text-emerald-700"><LogIn className="w-3.5 h-3.5" /> Check-in</span>
            <span className="inline-flex items-center gap-1 text-blue-700"><LogOut className="w-3.5 h-3.5" /> Check-out</span>
            <span className="inline-flex items-center gap-1 text-indigo-700"><Navigation className="w-3.5 h-3.5" /> Approx distance (haversine from GPS)</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
          <TimelineCard label="Case A · 1 place visit" start={START} places={CASE_1} />
          <TimelineCard label="Case B · 3 place visit" start={START} places={CASE_3} />
          <TimelineCard label="Case C · 5 place visit" start={START} places={CASE_5} />
        </div>

        <p className="text-xs text-slate-400 mt-6 max-w-3xl">
          Notes: distances are computed with the haversine formula between consecutive GPS points
          (start→first check-in, check-in→check-out on-site, and each check-out→next check-in shown as
          “from last checkout”). Where a check-in or check-out has no GPS captured, that segment will show
          “distance unavailable”. Approve this look and I'll wire it to real claim data.
        </p>
      </div>
    </div>
  );
}
